/**
 * McpClientsTable — list of OAuth clients that have connected to the
 * tenant's MCP gateway. Rows are clickable → open the client drawer.
 * Revoke uses a contextual confirm dialog with derived effect bullets.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ScopeChip } from "./ScopeChip";
import { McpClientDrawer } from "./McpClientDrawer";
import { RevokeMcpClientDialog } from "./RevokeMcpClientDialog";
import { useRevokeMcpClient } from "@/hooks/security/useMcpAdmin";
import { formatDistanceToNow } from "date-fns";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import type { McpClientDTO } from "@/types/edrApi";

const STATUS_TONE: Record<string, string> = {
  active: "bg-success-muted text-success-muted-foreground border-success-border",
  revoked: "bg-muted/40 text-muted-foreground border-border",
  expired: "bg-warning-muted text-warning-muted-foreground border-warning-border",
};

const TYPE_LABEL: Record<string, string> = {
  dcr: "DCR",
  "first-party": "1P",
  service: "SVC",
};

export interface McpClientsTableProps {
  clients: McpClientDTO[];
}

export function McpClientsTable({ clients }: McpClientsTableProps) {
  const revoke = useRevokeMcpClient();
  const [confirm, setConfirm] = useState<McpClientDTO | null>(null);
  const [drawer, setDrawer] = useState<McpClientDTO | null>(null);
  const sorted = [...clients].sort((a, b) => {
    const av = a.lastUsedAt ? new Date(a.lastUsedAt).getTime() : 0;
    const bv = b.lastUsedAt ? new Date(b.lastUsedAt).getTime() : 0;
    return bv - av;
  });

  return (
    <div className="overflow-x-auto -mx-4 -my-4">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <Th>Client</Th>
            <Th>Registered</Th>
            <Th>Last activity</Th>
            <Th>Scopes</Th>
            <Th align="right">Calls 24h</Th>
            <Th>Status</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {sorted.map((c) => {
            const id = c.id ?? c.displayName ?? Math.random().toString(36);
            const status = (c.status ?? "active").toLowerCase();
            const tone = STATUS_TONE[status] ?? STATUS_TONE.active;
            const scopes = c.scopes ?? [];
            const isRevoked = status === "revoked";
            const typeLabel = c.clientType ? (TYPE_LABEL[c.clientType] ?? c.clientType.toUpperCase()) : null;
            return (
              <tr
                key={id}
                onClick={() => setDrawer(c)}
                className="border-b border-border/60 align-top hover:bg-muted/40 cursor-pointer"
              >
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-foreground">
                      {c.displayName ?? "Unnamed client"}
                    </span>
                    {typeLabel && (
                      <span className="inline-flex items-center rounded border border-border bg-card px-1 py-0.5 font-mono text-[9px] text-muted-foreground">
                        {typeLabel}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                    {c.id ?? "—"}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  <div>
                    {c.registeredAt
                      ? formatDistanceToNow(new Date(c.registeredAt), { addSuffix: true })
                      : "—"}
                  </div>
                  {c.registeredBy && (
                    <div className="mt-0.5 text-[10px]">by {c.registeredBy}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  <div>
                    {c.lastUsedAt
                      ? formatDistanceToNow(new Date(c.lastUsedAt), { addSuffix: true })
                      : "Never"}
                  </div>
                  {c.lastUsedTool && (
                    <div className="mt-0.5 font-mono text-[10px]">{c.lastUsedTool}</div>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {scopes.length === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {scopes.slice(0, 6).map((s) => (
                        <ScopeChip key={s} scope={s} />
                      ))}
                      {scopes.length > 6 && (
                        <span className="text-[10px] text-muted-foreground">
                          +{scopes.length - 6}
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                  {(c.callsLast24h ?? 0).toLocaleString()}
                </td>
                <td className="px-3 py-2.5">
                  <span
                    className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] capitalize ${tone}`}
                  >
                    {status}
                  </span>
                </td>
                <td
                  className="px-3 py-2.5 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        aria-label="Client actions"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setDrawer(c)}>
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={isRevoked || revoke.isPending}
                        onClick={() => setConfirm(c)}
                        className="text-destructive focus:text-destructive"
                      >
                        Revoke client
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <McpClientDrawer
        client={drawer}
        open={!!drawer}
        onOpenChange={(o) => (o ? null : setDrawer(null))}
      />

      <RevokeMcpClientDialog
        client={confirm}
        open={!!confirm}
        onOpenChange={(o) => (o ? null : setConfirm(null))}
        pending={revoke.isPending}
        onConfirm={() => {
          if (!confirm?.id) return;
          revoke.mutate({ id: confirm.id, displayName: confirm.displayName });
          setConfirm(null);
        }}
      />
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children?: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`px-3 py-2 text-${align} text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}
    >
      {children}
    </th>
  );
}
